import React from 'react';
import type { ValuationTableWidgetInstanceConfig, ValuationTableData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatPercent, formatMultiple, formatFloat } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned).
const SAMPLE_DATA: ValuationTableData = {
  gross_margin: 0.4786,
  dividend_yield: 0.0034,
  pe_ratio: 37.06,
  peg_ratio: 1.28,
  ev_ebitda: 28.59,
  current_ratio: 1.07,
  quick_ratio: 1.02,
  debt_equity_ratio: 0.80,
  debt_asset_ratio: 0.23,
  ebit_interest_coverage: 18.4,
  ebitda_interest_coverage: null,
};

interface Props {
  config: ValuationTableWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

// A fraction like 0.4786 -> "47.9%"; null -> "--".
const pct = (v: number | null) => (v == null ? '--' : formatPercent(v * 100, 1));

const ValuationTableWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<ValuationTableData>(
    'valuation_table',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!d) return null;

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Gross Margin', value: pct(d.gross_margin) },
    { label: 'Dividend Yield', value: pct(d.dividend_yield) },
    { label: 'P/E', value: formatMultiple(d.pe_ratio) },
    { label: 'PEG', value: formatMultiple(d.peg_ratio) },
    { label: 'EV/EBITDA', value: formatMultiple(d.ev_ebitda) },
    { label: 'Current Ratio', value: formatFloat(d.current_ratio, 2) },
    { label: 'Quick Ratio', value: formatFloat(d.quick_ratio, 2) },
    { label: 'Debt/Equity', value: formatFloat(d.debt_equity_ratio, 2) },
    { label: 'Debt/Assets', value: formatFloat(d.debt_asset_ratio, 2) },
    { label: 'Interest Coverage', value: formatMultiple(d.ebit_interest_coverage) },
  ];

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">Valuation Multiples</span>
        <span className="text-[10px] uppercase tracking-wide text-neutral-400 ml-auto">TTM</span>
      </div>
      <div className="flex-1 overflow-auto">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-3">
          {rows.map(item => (
            <div key={item.label} className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">{item.label}</dt>
              <dd className="text-sm font-semibold text-neutral-800 mt-0.5">{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

export default ValuationTableWidgetRenderer;
